import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../lib/api.js';

// Shift discrepancy report (CJ 2026-09-22).
// Spec: docs/SHIFT-DISCREPANCY-REPORT-SPEC.md
//
// Schedule vs time clock for the payroll week: call-outs, no-shows, tardy,
// and anything more than 5 minutes off at either end.
//
// Collapsed by default. It is a read-while-you-process panel, not a blocker —
// nothing here stops a check from being correct, and a panel that shouts every
// week stops being read.
//
// A top-level component, not one declared inside PayrollReportView's render:
// a component defined in a render body is a new type on every keystroke, so
// React remounts it and any focus is lost.

// Only the system's existing attendance rules raise a flag: no-show, call-out,
// and the two lateness tiers reliability-emit.js already uses. The end of a
// shift is printed on every row and never flagged — shared/reliability.js
// records CJ's standing decision that leaving early is not a problem ("CJ
// encourages leaving early to save labor cost").
//
// no_clock_out and unscheduled are here as broken records rather than
// judgements: one makes a row's hours unknowable, the other is paid time with
// no shift behind it. Neither is a mark against anybody.
const FLAG_META = {
  no_show:      { label: 'No show',      tone: 'bad',  rank: 0 },
  call_out:     { label: 'Call-out',     tone: 'warn', rank: 1 },
  late_in_30:   { label: 'Late 30+',     tone: 'bad',  rank: 2 },
  late_in:      { label: 'Late in',      tone: 'warn', rank: 3 },
  no_clock_out: { label: 'No clock-out', tone: 'bad',  rank: 4 },
  unscheduled:  { label: 'Unscheduled',  tone: 'warn', rank: 5 },
};

const TONE = {
  bad:     { bg: '#faeceb', fg: '#a8323a', bd: '#f0cfcd' },
  warn:    { bg: '#fdf3e3', fg: '#8a5a1f', bd: '#f0ddba' },
  neutral: { bg: '#eef1f4', fg: '#4a5560', bd: '#dde2e8' },
};

function Chip({ flag }) {
  const meta = FLAG_META[flag] || { label: flag, tone: 'neutral' };
  const t = TONE[meta.tone];
  return (
    <span style={{
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, borderRadius: 999,
      padding: '1px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{meta.label}</span>
  );
}

const signed = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${n}m`);
const hhmm = (mins) => {
  if (!Number.isFinite(mins)) return '—';
  const sign = mins < 0 ? '-' : '';
  const a = Math.abs(mins);
  return `${sign}${Math.floor(a / 60)}h ${String(a % 60).padStart(2, '0')}m`;
};

export function ShiftDiscrepancyReport({ weekStart, currentUser, onDownloadPdf }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [onlyFlagged, setOnlyFlagged] = useState(true);

  const load = useCallback(async () => {
    setErr(''); setLoading(true);
    try {
      const r = await api('GET', `/api/admin/payroll/shift-discrepancies?week_start=${weekStart}`, null, headers);
      setData(r);
    } catch (e) {
      let m = e?.message || String(e);
      try { m = JSON.parse(m).error || m; } catch (_) { /* not JSON */ }
      setErr(m);
    } finally { setLoading(false); }
  }, [weekStart, currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (weekStart) load(); }, [weekStart, load]);

  const rows = useMemo(() => {
    const all = data?.rows || [];
    const shown = onlyFlagged ? all.filter((r) => r.flags.length > 0) : all;
    // Worst first — a no-show is the row that needs an answer before a
    // 6-minute late-in does.
    return [...shown].sort((a, b) => {
      const ra = Math.min(...a.flags.map((f) => FLAG_META[f]?.rank ?? 99), 99);
      const rb = Math.min(...b.flags.map((f) => FLAG_META[f]?.rank ?? 99), 99);
      return ra - rb || a.shift_date.localeCompare(b.shift_date) || a.name.localeCompare(b.name);
    });
  }, [data, onlyFlagged]);

  if (err) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #c62828' }}>
        <div className="text-base font-semibold mb-1">Shift discrepancies</div>
        <div style={{ fontSize: 12, color: '#a8323a' }}>{err}</div>
      </div>
    );
  }
  if (!data && loading) {
    return (
      <div className="card" style={{ borderLeft: '4px solid #dde2e8' }}>
        <div style={{ fontSize: 13, color: '#757575' }}>Shift discrepancies — loading…</div>
      </div>
    );
  }
  if (!data) return null;

  const s = data.summary;
  const attendance = s.no_show + s.call_out + s.late_in + s.late_in_30;

  return (
    <div className="card" style={{ borderLeft: `4px solid ${s.no_show > 0 ? '#c62828' : attendance > 0 ? '#b48a47' : '#2e7d32'}` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <div className="text-base font-semibold">
          Shift discrepancies
          {s.flagged === 0
            ? <span style={{ color: '#2e5d42' }}> · all clear</span>
            : <span style={{ color: '#8a5a1f' }}> · {s.flagged} of {s.shifts + s.unscheduled}</span>}
        </div>
        <span style={{ display: 'flex', gap: 6 }}>
          {onDownloadPdf && (
            <button onClick={() => onDownloadPdf('discrepancies')} style={{
              background: '#fff', border: '1px solid #d0d4d9', borderRadius: 7, padding: '4px 12px',
              fontSize: 12, fontWeight: 600, color: '#555', cursor: 'pointer', fontFamily: 'inherit',
            }}>PDF</button>
          )}
          <button onClick={() => setOpen((v) => !v)} style={{
            background: '#fff', border: '1px solid #d0d4d9', borderRadius: 7, padding: '4px 12px',
            fontSize: 12, fontWeight: 600, color: '#555', cursor: 'pointer', fontFamily: 'inherit',
          }}>{open ? 'Hide' : 'Show detail'}</button>
        </span>
      </div>

      <div style={{ fontSize: 12, color: '#757575', margin: '4px 0 10px' }}>
        Monarch schedule vs the time clocks, {data.week_start} – {data.week_end}. Late above{' '}
        {data.tardy_min} minutes, seriously late above {data.tardy_severe_min} — the same thresholds as
        the reliability score. Clock-out times are shown for reference: leaving early is not counted as
        a problem. Informational; it does not block payroll and writes no reliability incident.
      </div>

      {/* The headline counts. Attendance first because that is the question
          CJ asked; timing deviations are the money question underneath. */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, marginBottom: 4 }}>
        <span><b style={{ color: s.no_show ? '#a8323a' : '#37474f' }}>{s.no_show}</b> no show</span>
        <span><b style={{ color: s.call_out ? '#8a5a1f' : '#37474f' }}>{s.call_out}</b> call-out</span>
        <span><b style={{ color: s.late_in_30 ? '#a8323a' : '#37474f' }}>{s.late_in_30}</b> late 30+</span>
        <span><b style={{ color: s.late_in ? '#8a5a1f' : '#37474f' }}>{s.late_in}</b> late 10+</span>
        {s.unscheduled > 0 && <span><b>{s.unscheduled}</b> unscheduled</span>}
        {s.no_clock_out > 0 && <span style={{ color: '#a8323a' }}><b>{s.no_clock_out}</b> never clocked out</span>}
      </div>
      <div style={{ fontSize: 12, color: '#546069', marginBottom: open ? 10 : 0 }}>
        Scheduled {hhmm(s.scheduled_minutes)} · clocked {hhmm(s.actual_minutes)} ·{' '}
        <b style={{ color: s.delta_minutes > 0 ? '#8a5a1f' : s.delta_minutes < 0 ? '#2e5d42' : '#37474f' }}>
          {s.delta_minutes > 0 ? '+' : ''}{hhmm(s.delta_minutes)}
        </b>{' '}
        {s.delta_minutes > 0 ? 'over the schedule' : s.delta_minutes < 0 ? 'under the schedule' : 'on the schedule'}
        {s.no_clock_out > 0 && ' (open punches excluded)'}
        {/* Reported, never flagged — see FLAG_META. */}
        <span style={{ color: '#90a0ad' }}> · </span>
        left early {hhmm(s.left_early_minutes)} · stayed late {hhmm(s.stayed_late_minutes)}
      </div>

      {open && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', margin: '4px 0 8px' }}>
            <label style={{ fontSize: 12, color: '#546069', display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
              <input type="checkbox" checked={onlyFlagged} onChange={(e) => setOnlyFlagged(e.target.checked)} />
              Only shifts with a deviation
            </label>
            <button onClick={load} disabled={loading} style={{
              background: 'none', border: 'none', color: '#4a6fa5', fontSize: 12,
              cursor: 'pointer', fontFamily: 'inherit', padding: 0, textDecoration: 'underline',
            }}>{loading ? 'refreshing…' : 'refresh'}</button>
          </div>

          {rows.length === 0 ? (
            <div style={{ fontSize: 12.5, color: '#2e5d42' }}>
              No call-outs, no-shows, or late arrivals this week.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#757575', borderBottom: '1px solid #e6e6e6' }}>
                    <th style={{ padding: '5px 8px 5px 0' }}>Date</th>
                    <th style={{ padding: '5px 8px' }}>Who</th>
                    <th style={{ padding: '5px 8px' }}>Where</th>
                    <th style={{ padding: '5px 8px' }}>Scheduled</th>
                    <th style={{ padding: '5px 8px' }}>Clocked</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>In</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>Out</th>
                    <th style={{ padding: '5px 8px', textAlign: 'right' }}>Hours</th>
                    <th style={{ padding: '5px 0 5px 8px' }}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={`${r.user_id}-${r.shift_date}-${r.scheduled_start || r.actual_start}-${r.punch_id || 'none'}`}
                        style={{ borderBottom: '1px solid #f2f2f2' }}>
                      <td style={{ padding: '5px 8px 5px 0', whiteSpace: 'nowrap' }}>{r.shift_date}</td>
                      <td style={{ padding: '5px 8px', fontWeight: 600 }}>{r.name}</td>
                      <td style={{ padding: '5px 8px', color: '#757575' }}>{r.location_name || '—'}</td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                        {r.scheduled_start ? `${r.scheduled_start}–${r.scheduled_end}` : '—'}
                      </td>
                      <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                        {r.actual_start ? `${r.actual_start}–${r.actual_end || '??'}` : '—'}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: r.in_delta_min > 0 ? '#a8323a' : '#546069' }}>
                        {signed(r.in_delta_min)}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', color: '#546069' }}>
                        {signed(r.out_delta_min)}
                      </td>
                      <td style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {hhmm(r.actual_minutes)}
                        {r.scheduled_minutes > 0 && (
                          <span style={{ color: '#90a0ad' }}> / {hhmm(r.scheduled_minutes)}</span>
                        )}
                      </td>
                      <td style={{ padding: '5px 0 5px 8px' }}>
                        <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                          {r.flags.map((f) => <Chip key={f} flag={f} />)}
                          {r.absence_reason && (
                            <span style={{ fontSize: 11, color: '#757575' }}>{r.absence_reason}</span>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.by_employee.some((e) => e.flagged > 0) && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #e6e6e6' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#37474f', marginBottom: 5 }}>By person</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {data.by_employee.filter((e) => e.flagged > 0).map((e) => (
                  <div key={e.user_id} style={{ fontSize: 12.5, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <b style={{ minWidth: 150 }}>{e.name}</b>
                    <span style={{ color: '#546069' }}>
                      {e.flagged} of {e.shifts + e.unscheduled} ·{' '}
                      {[
                        e.no_show && `${e.no_show} no show`,
                        e.call_out && `${e.call_out} call-out`,
                        e.late_in_30 && `${e.late_in_30} late 30+`,
                        e.late_in && `${e.late_in} late 10+`,
                        e.unscheduled && `${e.unscheduled} unscheduled`,
                        e.no_clock_out && `${e.no_clock_out} open punch`,
                      ].filter(Boolean).join(' · ')}
                      {' · '}
                      <span style={{ color: e.delta_minutes > 0 ? '#8a5a1f' : '#2e5d42' }}>
                        {e.delta_minutes > 0 ? '+' : ''}{hhmm(e.delta_minutes)} vs schedule
                      </span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
